import React, { useState } from 'react';
import { X, Share, PlusSquare, MoreVertical, Smartphone, CheckCircle2, ArrowRight } from 'lucide-react';

interface PwaInstallInstructionsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const PwaInstallInstructionsModal: React.FC<PwaInstallInstructionsModalProps> = ({
  isOpen,
  onClose,
}) => {
  const isIOS = typeof navigator !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent);
  const [platform, setPlatform] = useState<'ios' | 'android'>(isIOS ? 'ios' : 'android');

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/75 backdrop-blur-md flex items-center justify-center p-4 animate-fade-in font-[-apple-system,BlinkMacSystemFont,'SF_Pro_Text',sans-serif]">
      <div className="bg-white dark:bg-[#12141A] text-slate-900 dark:text-white rounded-[28px] max-w-sm w-full border border-slate-200 dark:border-zinc-800 shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-5 py-4 flex items-center justify-between border-b border-slate-100 dark:border-zinc-800">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 rounded-xl bg-teal-500/10 text-teal-600 dark:text-teal-400 flex items-center justify-center">
              <Smartphone className="w-4 h-4" />
            </div>
            <h3 className="text-sm font-black uppercase tracking-tight">Add to Home Screen</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-slate-100 dark:bg-zinc-800 flex items-center justify-center text-slate-500 dark:text-zinc-400 hover:text-rose-500 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Platform Toggle */}
        <div className="p-4 pb-0">
          <div className="grid grid-cols-2 p-1 bg-slate-100 dark:bg-zinc-900 rounded-2xl text-xs font-bold">
            <button
              type="button"
              onClick={() => setPlatform('ios')}
              className={`py-2 rounded-xl transition-all ${
                platform === 'ios'
                  ? 'bg-white dark:bg-zinc-800 text-teal-600 dark:text-teal-400 shadow-xs'
                  : 'text-slate-500 dark:text-zinc-400'
              }`}
            >
              iPhone / iPad (Safari)
            </button>
            <button
              type="button"
              onClick={() => setPlatform('android')}
              className={`py-2 rounded-xl transition-all ${
                platform === 'android'
                  ? 'bg-white dark:bg-zinc-800 text-teal-600 dark:text-teal-400 shadow-xs'
                  : 'text-slate-500 dark:text-zinc-400'
              }`}
            >
              Android (Chrome)
            </button>
          </div>
        </div>

        {/* Instructions Body */}
        <div className="p-5 space-y-4 text-xs">
          {platform === 'ios' ? (
            <div className="space-y-3.5">
              <p className="text-slate-500 dark:text-zinc-400 text-[11px] leading-relaxed">
                Install DigestLog as a fullscreen native-like app on your iPhone:
              </p>

              <div className="flex items-start space-x-3 p-3 rounded-2xl bg-slate-50 dark:bg-[#1A1D24] border border-slate-100 dark:border-zinc-800">
                <div className="w-6 h-6 rounded-lg bg-teal-500 text-white flex items-center justify-center font-bold text-xs shrink-0 mt-0.5">
                  1
                </div>
                <div className="min-w-0">
                  <p className="font-bold text-slate-900 dark:text-white flex items-center gap-1.5">
                    <span>Tap the</span>
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-slate-200 dark:bg-zinc-700 text-slate-800 dark:text-white font-black text-[10px]">
                      <Share className="w-3 h-3 inline mr-1" /> Share
                    </span>
                    <span>button</span>
                  </p>
                  <p className="text-[11px] text-slate-500 dark:text-zinc-400 mt-0.5">
                    Located in Safari's bottom toolbar (or top bar on iPad).
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3 p-3 rounded-2xl bg-slate-50 dark:bg-[#1A1D24] border border-slate-100 dark:border-zinc-800">
                <div className="w-6 h-6 rounded-lg bg-teal-500 text-white flex items-center justify-center font-bold text-xs shrink-0 mt-0.5">
                  2
                </div>
                <div className="min-w-0">
                  <p className="font-bold text-slate-900 dark:text-white flex items-center gap-1.5">
                    <span>Scroll down and tap</span>
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-slate-200 dark:bg-zinc-700 text-slate-800 dark:text-white font-black text-[10px]">
                      <PlusSquare className="w-3 h-3 inline mr-1" /> Add to Home Screen
                    </span>
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3 p-3 rounded-2xl bg-slate-50 dark:bg-[#1A1D24] border border-slate-100 dark:border-zinc-800">
                <div className="w-6 h-6 rounded-lg bg-teal-500 text-white flex items-center justify-center font-bold text-xs shrink-0 mt-0.5">
                  3
                </div>
                <div className="min-w-0">
                  <p className="font-bold text-slate-900 dark:text-white">
                    Tap <strong>"Add"</strong> in the top right corner.
                  </p>
                  <p className="text-[11px] text-slate-500 dark:text-zinc-400 mt-0.5">
                    DigestLog will appear on your Home Screen with instant offline startup!
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-3.5">
              <p className="text-slate-500 dark:text-zinc-400 text-[11px] leading-relaxed">
                Install DigestLog as a standalone app on your Android device:
              </p>

              <div className="flex items-start space-x-3 p-3 rounded-2xl bg-slate-50 dark:bg-[#1A1D24] border border-slate-100 dark:border-zinc-800">
                <div className="w-6 h-6 rounded-lg bg-teal-500 text-white flex items-center justify-center font-bold text-xs shrink-0 mt-0.5">
                  1
                </div>
                <div className="min-w-0">
                  <p className="font-bold text-slate-900 dark:text-white flex items-center gap-1.5">
                    <span>Tap the</span>
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-slate-200 dark:bg-zinc-700 text-slate-800 dark:text-white font-black text-[10px]">
                      <MoreVertical className="w-3 h-3 inline mr-0.5" /> Menu (⋮)
                    </span>
                    <span>in Chrome</span>
                  </p>
                  <p className="text-[11px] text-slate-500 dark:text-zinc-400 mt-0.5">
                    Located in the top right corner of your browser.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3 p-3 rounded-2xl bg-slate-50 dark:bg-[#1A1D24] border border-slate-100 dark:border-zinc-800">
                <div className="w-6 h-6 rounded-lg bg-teal-500 text-white flex items-center justify-center font-bold text-xs shrink-0 mt-0.5">
                  2
                </div>
                <div className="min-w-0">
                  <p className="font-bold text-slate-900 dark:text-white">
                    Tap <strong>"Install App"</strong> or <strong>"Add to Home screen"</strong>.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3 p-3 rounded-2xl bg-slate-50 dark:bg-[#1A1D24] border border-slate-100 dark:border-zinc-800">
                <div className="w-6 h-6 rounded-lg bg-teal-500 text-white flex items-center justify-center font-bold text-xs shrink-0 mt-0.5">
                  3
                </div>
                <div className="min-w-0">
                  <p className="font-bold text-slate-900 dark:text-white">
                    Confirm <strong>"Install"</strong>.
                  </p>
                  <p className="text-[11px] text-slate-500 dark:text-zinc-400 mt-0.5">
                    The app launches fullscreen without any browser address bar.
                  </p>
                </div>
              </div>
            </div>
          )}

          <button
            type="button"
            onClick={onClose}
            className="w-full py-3 rounded-2xl bg-slate-950 dark:bg-white text-white dark:text-black font-black text-xs uppercase tracking-wider shadow-sm active:scale-95 transition-all"
          >
            Got It
          </button>
        </div>
      </div>
    </div>
  );
};
